<script setup lang="ts">
import { ChevronRight } from 'lucide-vue-next'

const featured = [
  { title: 'Genworth Insurance', category: 'Insurance', built: 'Policy management, staff training portals and a company intranet.', thumb: '/images/projects/thumbs/genworth.webp', full: '/images/projects/genworth/gworth_1.webp', fullWidth: 800 },
  { title: 'Franklin Templeton', category: 'Finance', built: 'Investor portal with secure documents and fund dashboards.', thumb: '/images/projects/thumbs/franklin.webp', full: '/images/projects/franklin/fti_1.webp', fullWidth: 800 },
  { title: 'Thorntons Solicitors', category: 'Legal', built: 'Conveyancing and property transactions for one of Scotland\'s largest law firms.', thumb: '/images/projects/thumbs/thorntons.webp', full: '/images/projects/thorntons/thorn_1.webp', fullWidth: 480 },
  { title: 'Newbury College', category: 'Education', built: 'Enrolment, scheduling, Stripe billing and a parent portal.', thumb: '/images/projects/thumbs/newbury.webp', full: '/images/projects/newbury/newb_1.webp', fullWidth: 800 },
]
</script>

<template>
  <section id="work" class="py-20 md:py-40 relative">
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blu-500/[0.02] rounded-full blur-3xl pointer-events-none" />

    <div class="max-w-7xl mx-auto px-6 md:px-12">
      <div class="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <!-- Left: Content -->
        <div data-aos="fade">
          <div class="flex items-center gap-3 mb-6">
            <span class="text-xs tracking-[0.25em] text-blu-900 dark:text-blu-400 uppercase font-medium">Portfolio</span>
          </div>

          <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-700 dark:text-white mb-6">
            Selected work
          </h2>

          <p class="text-lg md:text-xl text-slate-700/80 dark:text-white/65 leading-relaxed text-pretty max-w-xl mb-10">
            Over 150 projects for insurers, law firms, universities and government, from AI-powered SaaS to enterprise platforms, many of them still running smoothly years after launch.
          </p>

          <NuxtLink
            to="/work"
            class="font-medium text-blu-600 underline decoration-dotted underline-offset-4 transition-colors hover:decoration-solid dark:text-blu-400"
          >
            View all projects
          </NuxtLink>
        </div>

        <!-- Right: the projects as rows divided by hairlines, on the same dot
             stage as the other sections. Thumbnails shown at half their
             captured width, so they stay sharp. -->
        <VisualStage>
          <ul class="relative divide-y divide-slate-900/10 dark:divide-white/10">
            <li v-for="(project, index) in featured" :key="project.title" data-aos="fade" :data-aos-delay="150 + index * 100">
              <NuxtLink to="/work" class="group flex flex-col gap-4 py-6 transition-colors duration-300 hover:bg-blu-500/[0.06] sm:flex-row sm:items-center sm:gap-5 sm:px-3 sm:py-5">
                <!-- On a phone a strip off the top of the screenshot runs full width above
                     the text, from the
                     larger capture; from sm the small thumbnail sits beside it. -->
                <img
                  :src="project.thumb"
                  :srcset="`${project.thumb} 240w, ${project.full} ${project.fullWidth}w`"
                  sizes="(min-width: 640px) 120px, calc(100vw - 48px)"
                  :alt="project.title"
                  width="240"
                  height="150"
                  loading="lazy"
                  class="aspect-[16/7] w-full shrink-0 rounded-sm object-cover object-top sm:aspect-auto ring-1 ring-slate-900/10 dark:ring-white/10 sm:h-[75px] sm:w-[120px]"
                />
                <div class="min-w-0 flex-1">
                  <!-- On a phone the category sits under the title. -->
                  <div class="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
                    <h3 class="text-lg font-semibold text-slate-700 transition-colors group-hover:text-blu-600 dark:text-white dark:group-hover:text-blu-400">{{ project.title }}</h3>
                    <span class="text-xs text-slate-700/75 dark:text-white/50">{{ project.category }}</span>
                  </div>
                  <p class="mt-1 text-[15px] leading-relaxed text-slate-700/75 dark:text-white/55">{{ project.built }}</p>
                </div>
                <ChevronRight class="hidden h-4 w-4 shrink-0 sm:block -translate-x-1 text-blu-500 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
              </NuxtLink>
            </li>
          </ul>
        </VisualStage>
      </div>
    </div>
  </section>
</template>
